import { EWorkshopChildcareAgeGroups, TChildcareItem } from '@packages/utils';
import { TAgeGroupColumnItem } from '../../components/Shared/AgeGroupesColumn/AgeGroupesColumn.types';
import { EAgeGroup, EWorkshopLanguage, TTableFilters } from './generic.types';
import {
  EWorkshopCategory,
  EWorkshopPartType,
  EWorkshopSeriesType,
  EWorkshopStandaloneType,
  TPartsUI,
} from './workshops.types';
import { TTagDB, TTagUI } from './tags.types';
import { EEventPartType } from './events.types';

export enum ERegitrantStatus {
  ACTIVE = 'ACTIVE',
  UNREGISTERED = 'UNREGISTERED',
}

export enum EAttendanceStatus {
  ATTENDED = 'ATTENDED',
  ABSENT = 'ABSENT',
  E_LEARNING = 'E_LEARNING',
}

export enum EAttendanceState {
  NOT_YET_OPEN = 'NOT_YET_OPEN',
  OPEN = 'OPEN',
  CLOSED = 'CLOSED',
}
export enum ERegistrantsColumnKey {
  CREATION_DATE = 'creationDate',
  FIRST_NAME = 'firstName',
  LAST_NAME = 'lastName',
  AGE_GROUP = 'ageGroups',
  LANGUAGE = 'languages',
  FILTER_BY_LANGUAGE_AND_AGE_GROUP = 'FILTER_BY_LANGUAGE_AND_AGE_GROUP',
  REGISTRANT_FULL_NAME = 'registrantFullName',
  ADDITIONAL_ADULT_FULL_NAME = 'additionalAdultFullName',
  EMAIL = 'email',
  ADDITIONAL_ADULT_EMAIL = 'additionalAdultEmail',
}

export enum EWaitListColumnKey {
  CREATION_DATE = 'creationDate',
  AGE_GROUP = 'ageGroups',
  TYPE = 'workshopTypes',
  FIRST_NAME = 'firstName',
  LAST_NAME = 'lastName',
  EMAIL = 'email',
  CITIES = 'cities',
  LOCATION = 'locationIds',
  TAGS = 'tagIds',
}

export enum EAttendanceColumnKey {
  FIRST_NAME = 'firstName',
  LAST_NAME = 'lastName',
  PHOTO = 'photo',
  CHILDREN = 'children',
  AGE_GROUP = 'ageGroups',
  MATERIALS_LANGUAGE = 'materialsLanguage',
  MATERIALS_LANGUAGE_FILTER = 'materialsLanguageFilter',
  ATTENDANCE = 'attendance',
  KITS = 'kits',
  BINDERS = 'binders',
  NOTIFICATIONS = 'notifications',
  EMAIL = 'email',
  PHONE = 'phone',
}

export enum EChildcareColumnKey {
  FIRST_NAME = 'firstName',
  LAST_NAME = 'lastName',
}

export type TOrientationKitUI = {
  requested: boolean;
  received: boolean;
};

export type TAttendancesGetDB = {
  sessionAttendanceId: string;
  partAttendanceId?: string;
  partDateTime?: string;
  attendanceStatus: EAttendanceStatus;
  kitCount: number | null;
  sessionId: string;
  partType: EWorkshopPartType;
  isUpdatable: boolean;
  /** GROW-4321 / GROW-4332: three-state attendance window computed server-side
   *  in the workshop/event's own zone. */
  attendanceState?: EAttendanceState;
  attendanceOpensAt?: string;
  attendanceClosesAt?: string;
};

export type TAttendancesGetUI = {
  sessionAttendanceId: string;
  partAttendanceId?: string;
  partDateTime?: string;
  attendanceStatus: EAttendanceStatus;
  kitCount: number | null;
  sessionId: string;
  partType: EWorkshopPartType | EEventPartType;
  isUpdatable: boolean;
  attendanceState?: EAttendanceState;
  /** Pre-formatted for display. */
  attendanceOpensAt?: string;
  attendanceClosesAt?: string;
};

export type TRegistrantDB = {
  isWorkshopClosed?: boolean | undefined;
  hasAttendedWorkshop?: boolean | undefined;
  registrantId: string;
  lastName: string;
  firstName: string;
  email?: string | null;
  phone: string | null;
  createdDate: string;
  countOfChildren: number;
  childrenNames: string[] | null;
  attendances?: TAttendancesGetDB[];
  orientationKitReceived?: boolean;
  orientationKitRequested?: boolean;
  materialsLanguage: EWorkshopLanguage;
  language: EWorkshopLanguage;
  ageGroup: EAgeGroup;
  customQuestionAnswer?: string;
  workshopClassId: string;
  status: ERegitrantStatus;
  allowedTakePicture: boolean | null;
  nonDeleteReason?: string;
  notificationDisabled?: boolean;
  /** GROW-4363: server-masked minimal registrant (no contact info, no edit). */
  minimalRegistration?: boolean;
  additionalAdults: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    fullName: string;
  }[];
};

export type TRegistrantUI = {
  registrantId: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email?: string;
  phone: string;
  createdDate: string;
  countOfChildren: number;
  childrenNames: string[];
  attendances: TAttendancesGetUI[];
  hasUpdatableAttendance?: boolean;
  orientationKit?: TOrientationKitUI;
  materialsLanguage: EWorkshopLanguage;
  spanishAgeGroups: TAgeGroupColumnItem[];
  englishAgeGroups: TAgeGroupColumnItem[];
  customQuestionAnswer?: string;
  workshopClassId: string;
  registrantStatus: ERegitrantStatus;
  allowedTakePicture: boolean | null;
  sessionDateTime?: string;
  status?: ERegitrantStatus;
  isWorkshopClosed?: boolean;
  hasAttendedWorkshop?: boolean;
  nonDeleteReason?: string;
  notificationsEnabled?: boolean;
  /** GROW-4363: server-masked minimal registrant (no contact info, no edit). */
  minimalRegistration?: boolean;
  additionalAdults: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    fullName: string;
  }[];
};

export type TUpdateRegistrant = {
  workshopId: string;
  workshopClassId: string;
  registrantId: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone: string;
  materialsLanguage: EWorkshopLanguage;
  childNames: string[];
  childcareRequirements: TChildCare[];
  hasReadyBinder?: boolean;
  additionalAdults: TAdditionalAdults[];
};

export type TAttendanceEditUI = {
  sessionAttendanceId?: string;
  sessionId?: string;
  attendanceId?: string;
  kitCount: number | null;
  attendanceStatus: EAttendanceStatus;
  partType?: EWorkshopPartType;
  isUpdatable?: boolean;
  // for events
  partAttendanceId?: string;
  partId?: string;
  partDateTime?: string;
};

export type TAttendanceEditDB = {
  attendanceId: string;
  kitCount: number | null;
  attendanceStatus: EAttendanceStatus;
};

export type TAttendancesEditUI = {
  registrantId: string;
  attendances: TAttendanceEditUI[];
  orientationKitReceived?: boolean;
  workshopId: string;
};

export type TAttendancesEditDB = {
  workshopId: string;
  registrantId: string;
  attendances: TAttendanceEditDB[];
  orientationKitReceived?: boolean;
};

export type TAttendanceListUI = {
  firstName: string;
  lastName: string;
  photo: boolean | null;
  childrenNames: string[];
  spanishAgeGroups: TAgeGroupColumnItem[];
  englishAgeGroups: TAgeGroupColumnItem[];
  attendance: EAttendanceStatus;
  kits: number;
  binders: TOrientationKitUI;
  countOfChildren: number;
  registrantId: string;
  attendances: TAttendanceEditUI[];
  currentAttendanceSessionId?: string;
  sessionDateTime?: string;
  partDateTime?: string;
  sessions?: TPartsUI[];
  materialsLanguage: EWorkshopLanguage;
  notificationsEnabled?: boolean;
  customQuestionAnswer?: string;
  isEventClosed?: boolean;
  hasAttendedEvent?: boolean;
  nonDeleteReason?: string;
  email?: string;
  phone?: string;
  /** GROW-4363: server-masked minimal registrant (no contact info, no edit). */
  minimalRegistration?: boolean;
};

export type TWaitListUI = {
  registrantId: string;
  lastName: string;
  firstName: string;
  workshopTypeCategory: EWorkshopCategory;
  email: string;
  phone: string;
  workshopType: EWorkshopStandaloneType | EWorkshopSeriesType;
  spanishAgeGroups: TAgeGroupColumnItem[];
  englishAgeGroups: TAgeGroupColumnItem[];
  attendances: TAttendancesGetUI[];
  createdDate: string;
  childrenNames: string[];
  countOfChildren: number;
  city: string;
  workshopClassId: string;
  customQuestionAnswer?: string;
  location: string | null;
  workshopId: string;
  tags: TTagUI[];
};

export type TWaitListDB = {
  registrantId: string;
  language: EWorkshopLanguage;
  lastName: string;
  firstName: string;
  workshopTypeCategory: EWorkshopCategory;
  email: string;
  phone: string;
  workshopType: EWorkshopStandaloneType | EWorkshopSeriesType;
  ageGroup: EAgeGroup;
  attendances: TAttendancesGetDB[];
  createdDate: string;
  childrenNames: string[];
  countOfChildren: number;
  city: string;
  workshopClassId: string;
  customQuestionAnswer?: string;
  location: string | null;
  workshopId: string;
  tags: TTagDB[];
};

export type TWaitListMoveUI = {
  registrantId: string;
  workshopClassId: string;
  workshopId?: string;
};
export type TWaitListMoveDB = {
  registrantId: string;
  workshopClassId: string;
  workshopId?: string;
};

export type TWaitListMoveEventUI = {
  registrantId: string;
  eventClassId: string;
  eventId?: string;
};

export type TWaitListMoveEventDB = {
  registrantId: string;
  eventClassId: string;
  eventId?: string;
};

export interface TWaitListQueryParams
  extends TTableFilters<EWaitListColumnKey> {
  workshopId?: string;
  activeOnly?: boolean;
}

export type TRemoveRegistrantUI = {
  registrantId: string;
  workshopClassId: string;
  workshopId: string;
};
export type TRemoveRegistrantDB = {
  registrantId: string;
  workshopClassId: string;
  workshopId: string;
};

export type TRegisterRegistrantUI = {
  registrantId: string;
  workshopClassId: string;
  workshopId: string;
};
export type TRegisterRegistrantDB = {
  registrantId: string;
  workshopClassId: string;
  workshopId: string;
};

export type TChildcareRequirementsDB = {
  ageGroup: EWorkshopChildcareAgeGroups;
  count: number | null;
};

export type TRegistrantsWithChildcareDB = {
  lastName: string;
  firstName: string;
  workshopClassId: string;
  registrantId: string;
  childcareRequirements: TChildcareRequirementsDB[];
};

export type TRegistrantsWithChildcareUI = {
  lastName: string;
  firstName: string;
  workshopClassId: string;
  registrantId: string;
  childcareRequirements: TChildcareItem[];
};

export type TEditRegitrantChildcareUI = {
  registrantId: string;
  workshopClassId: string;
  childcareRequirements: TChildcareItem[];
  workshopId: string;
};
export type TEditRegitrantChildcareDB = {
  registrantId: string;
  workshopClassId: string;
  childcareRequirements: TChildcareRequirementsDB[];
  workshopId: string;
};

export type TRegistrantsNoChildcareDB = {
  lastName: string;
  firstName: string;
  workshopClassId: string;
  registrantId: string;
};

export type TRegistrantsNoChildcareUI = {
  fullName: string;
  workshopClassId: string;
  registrantId: string;
};

export type TAdditionalAdults = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
};

export type TChildCare = {
  ageGroup: EWorkshopChildcareAgeGroups;
  count: number | null;
};

export type TEventAttendancesEditUI = {
  registrantId: string;
  attendances: TAttendanceEditUI[];
  orientationKitReceived?: boolean;
  eventId: string;
};

export type TEventAttendancesEditDB = {
  eventId: string;
  registrantId: string;
  attendances: TAttendanceEditDB[];
  orientationKitReceived?: boolean;
};

export type TEventRegistrantNoChildcareUI = {
  fullName: string;
  eventClassId?: string;
  registrantId: string;
  eventId: string;
};

// Event-specific childcare types
export type TEventEditRegistrantChildcareUI = {
  registrantId: string;
  // eventClassId: string;
  childcareRequirements: TChildcareItem[];
  eventId: string;
};

export type TEventEditRegistrantChildcareDB = {
  registrantId: string;
  // eventClassId: string;
  childcareRequirements: TChildcareRequirementsDB[];
  eventId: string;
};

export type TEventRegistrantsWithChildcareDB = {
  lastName: string;
  firstName: string;
  eventClassId: string;
  registrantId: string;
  childcareRequirements: TChildcareRequirementsDB[];
};

export type TEventRegistrantsWithChildcareUI = {
  lastName: string;
  firstName: string;
  eventClassId: string;
  registrantId: string;
  childcareRequirements: TChildcareItem[];
};

export type TEventRegistrantsNoChildcareDB = {
  lastName: string;
  firstName: string;
  eventClassId?: string;
  registrantId: string;
  eventId: string;
};

export type TEventRemoveRegistrantUI = {
  registrantId: string;
  eventClassId: string;
  eventId: string;
};

export type TEventRemoveRegistrantDB = {
  registrantId: string;
  eventClassId: string;
  eventId: string;
};

export type TEventRegisterRegistrantUI = {
  registrantId: string;
  eventClassId: string;
  eventId: string;
};

export type TEventRegisterRegistrantDB = {
  registrantId: string;
  eventClassId: string;
  eventId: string;
};

// Event-specific registrant types
export type TEventRegistrantDB = {
  isEventClosed?: boolean | undefined;
  hasAttendedEvent?: boolean | undefined;
  registrantId: string;
  lastName: string;
  firstName: string;
  email?: string | null;
  phone: string | null;
  createdDate: string;
  countOfChildren: number;
  childrenNames: string[] | null;
  attendances?: TAttendancesGetDB[];
  orientationKitReceived?: boolean;
  orientationKitRequested?: boolean;
  materialsLanguage: EWorkshopLanguage;
  language: EWorkshopLanguage;
  ageGroup: EAgeGroup;
  customQuestionAnswer?: string;
  eventClassId: string;
  status: ERegitrantStatus;
  allowedTakePicture: boolean | null;
  nonDeleteReason?: string;
  notificationDisabled?: boolean;
  /** GROW-4363: server-masked minimal registrant (no contact info, no edit). */
  minimalRegistration?: boolean;
  additionalAdults: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    fullName: string;
  }[];
};

export type TEventRegistrantUI = {
  registrantId: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email?: string;
  phone: string;
  createdDate: string;
  countOfChildren: number;
  childrenNames: string[];
  attendances: TAttendancesGetUI[];
  hasUpdatableAttendance?: boolean;
  orientationKit?: TOrientationKitUI;
  materialsLanguage: EWorkshopLanguage;
  spanishAgeGroups: TAgeGroupColumnItem[];
  englishAgeGroups: TAgeGroupColumnItem[];
  customQuestionAnswer?: string;
  eventClassId: string;
  registrantStatus: ERegitrantStatus;
  allowedTakePicture: boolean | null;
  sessionDateTime?: string;
  status?: ERegitrantStatus;
  isEventClosed?: boolean;
  hasAttendedEvent?: boolean;
  nonDeleteReason?: string;
  notificationsEnabled?: boolean;
  /** GROW-4363: server-masked minimal registrant (no contact info, no edit). */
  minimalRegistration?: boolean;
  additionalAdults: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    fullName: string;
  }[];
};

export type TEventUpdateRegistrant = {
  eventId: string;
  eventClassId: string;
  registrantId: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone: string;
  materialsLanguage: EWorkshopLanguage;
  childNames: string[];
  childcareRequirements: TChildCare[];
  hasReadyBinder?: boolean;
  additionalAdults: TAdditionalAdults[];
};

export type TEventAttendanceListUI = {
  firstName: string;
  lastName: string;
  photo: boolean | null;
  childrenNames: string[];
  spanishAgeGroups: TAgeGroupColumnItem[];
  englishAgeGroups: TAgeGroupColumnItem[];
  attendance: EAttendanceStatus;
  kits: number;
  binders: TOrientationKitUI;
  countOfChildren: number;
  registrantId: string;
  attendances: TAttendanceEditUI[];
  currentAttendanceSessionId?: string;
  sessionDateTime?: string;
  sessions?: TPartsUI[];
  materialsLanguage: EWorkshopLanguage;
  notificationsEnabled?: boolean;
  customQuestionAnswer?: string;
  isEventClosed?: boolean;
  hasAttendedEvent?: boolean;
  nonDeleteReason?: string;
  email?: string;
  phone?: string;
  /** GROW-4363: server-masked minimal registrant (no contact info, no edit). */
  minimalRegistration?: boolean;
};

export type TEventWaitListUI = {
  registrantId: string;
  lastName: string;
  firstName: string;
  eventTypeCategory: EWorkshopCategory;
  email: string;
  phone: string;
  eventType: EWorkshopStandaloneType | EWorkshopSeriesType;
  spanishAgeGroups: TAgeGroupColumnItem[];
  englishAgeGroups: TAgeGroupColumnItem[];
  attendances: TAttendancesGetUI[];
  createdDate: string;
  childrenNames: string[];
  countOfChildren: number;
  city: string;
  eventClassId: string;
  customQuestionAnswer?: string;
  location: string | null;
  eventId: string;
  tags: TTagUI[];
  materialsLanguage: EWorkshopLanguage;
  registrantStatus: ERegitrantStatus;
  allowedTakePicture: boolean;
  status: ERegitrantStatus;
  additionalAdults: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    fullName: string;
  }[];
  hostName: string;
};

export type TEventWaitListDB = {
  registrantId: string;
  language: EWorkshopLanguage;
  lastName: string;
  firstName: string;
  eventTypeCategory: EWorkshopCategory;
  email: string;
  phone: string;
  eventType: EWorkshopStandaloneType | EWorkshopSeriesType;
  ageGroup: EAgeGroup;
  attendances: TAttendancesGetDB[];
  createdDate: string;
  childrenNames: string[];
  countOfChildren: number;
  city: string;
  eventClassId: string;
  customQuestionAnswer?: string;
  location: string | null;
  eventId: string;
  tags: TTagDB[];
  materialsLanguage: EWorkshopLanguage;
  status: ERegitrantStatus;
  allowedTakePicture: boolean;
  additionalAdults: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    fullName: string;
  }[];
  hostName: string;
};

export interface TEventWaitListQueryParams
  extends TTableFilters<EWaitListColumnKey> {
  eventId?: string;
  activeOnly?: boolean;
}

// GROW-4365: PATCH /workshops/registrants/materials-language. The BE moves any
// already-deducted kit/binder inventory from the old language to the new one.
export type TUpdateRegistrantMaterialsLanguageUI = {
  workshopId: string;
  registrantId: string;
  materialsLanguage: EWorkshopLanguage;
};
